import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import type { IxcDtccCnsV1Fail, IxcDtccCnsV1QvPair, IxcDtccCnsV1ReconciledTotals, IxcDtccCnsV1SettlementTransaction } from '@interxfi/types';
import { getNumberOrUndefinedFromNullable, getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getAssetSecurityListItems } from '../../asset/asset-list-items';
import { getAssetDetailPath } from '../../asset/asset-routing';

export function getCnsQvPairListItems(data: IxcDtccCnsV1QvPair | undefined, prefix = '') {
  return {
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', getNumericStringOrUndefinedFromNullable(data, 'quantity')),
    value: getReadonlyListItemCurrency(`${prefix}value`, 'Value', getNumericStringOrUndefinedFromNullable(data, 'value')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCnsFailListItems(data: IxcDtccCnsV1Fail | undefined, prefix = '') {
  return {
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', getNumericStringOrUndefinedFromNullable(data, 'quantity')),
    marketValue: getReadonlyListItemCurrency(`${prefix}marketValue`, 'Market Value', getNumericStringOrUndefinedFromNullable(data, 'marketValue')),
    tradeValue: getReadonlyListItemCurrency(`${prefix}tradeValue`, 'Trade Value', getNumericStringOrUndefinedFromNullable(data, 'tradeValue')),
    daysOpen: getReadonlyListItemNumber(`${prefix}daysOpen`, 'Days Open', getNumberOrUndefinedFromNullable(data, 'daysOpen')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCnsReconciledTotalsListItems(data: IxcDtccCnsV1ReconciledTotals | undefined, prefix = '') {
  return {
    openingFail: data?.openingFail ? getCnsFailListItems(data.openingFail, `${prefix}openingFail.`) : undefined,
    netTrade: data?.netTrade ? getCnsQvPairListItems(data.netTrade, `${prefix}netTrade.`) : undefined,
    settlingTrade: data?.settlingTrade ? getCnsQvPairListItems(data.settlingTrade, `${prefix}settlingTrade.`) : undefined,
    stockDividendsAndMiscQuantity: getReadonlyListItemNumber(
      `${prefix}stockDividendsAndMiscQuantity`,
      'Stock Dividends and Misc Quantity',
      data?.stockDividendsAndMiscQuantity,
    ),
    netSettlingQuantity: getReadonlyListItemNumber(`${prefix}netSettlingQuantity`, 'Net Settling Quantity', data?.netSettlingQuantity),
    closingFail: data?.closingFail ? getCnsFailListItems(data.closingFail, `${prefix}closingFail.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getCnsSettlementTransactionListItems(data: IxcDtccCnsV1SettlementTransaction | undefined, prefix = '') {
  return {
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    security: getAssetSecurityListItems(data?.security, `${prefix}security.`),
    ...getCnsReconciledTotalsListItems(data, prefix),
  } as const satisfies ReadonlyListItemRecord;
}
